import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookMarked, Trash2, ArrowRight, Search, Calendar, DollarSign, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, authHeaders, logout } = useAuth();
  const navigate = useNavigate();
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickSearch, setQuickSearch] = useState('');

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    fetchDashboard();
  }, [user]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/user/dashboard`, { headers: authHeaders });
      setDashData(res.data);
    } catch (e) {
      console.error('Failed to fetch dashboard', e);
    } finally {
      setLoading(false);
    }
  };

  const deleteSearch = async (id) => {
    try {
      await axios.delete(`${API}/user/saved-searches/${id}`, { headers: authHeaders });
      setDashData(prev => ({
        ...prev,
        saved_searches: prev.saved_searches.filter(s => s.id !== id),
      }));
      toast.success('Search removed.');
    } catch (e) {
      toast.error('Could not remove search.');
    }
  };

  const handleQuickSearch = (e) => {
    e.preventDefault();
    if (quickSearch.trim()) {
      navigate(`/search-results?q=${encodeURIComponent(quickSearch.trim())}`);
    }
  };

  const formatMemberSince = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } catch { return ''; }
  };

  if (!user) return null;

  const savedSearches = dashData?.saved_searches ?? [];
  const alertCount = dashData?.alert_count ?? 0;
  const firstName = dashData?.first_name || user.full_name?.split(' ')[0] || 'there';
  const memberSince = formatMemberSince(dashData?.member_since || user.created_at);

  return (
    <div className="min-h-screen bg-[#F0EEE9]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-2">My Account</p>
            <h1
              className="text-4xl font-medium text-stone-900"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Welcome back, {firstName}
            </h1>
            <p className="text-stone-500 mt-1 text-sm">
              {user.email}
              {memberSince && <span className="ml-2 text-[#A8B4B8]">· Member since {memberSince}</span>}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/procedures')}
              className="flex items-center gap-2 bg-[#0D3D4A] text-white px-6 py-3 text-sm font-medium hover:bg-[#0E4557] transition-colors"
              data-testid="new-search-btn"
            >
              <Search size={15} /> New Search
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">

        {/* Quick Search */}
        <form onSubmit={handleQuickSearch} className="mb-10 flex gap-3">
          <div className="relative flex-1 max-w-xl">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8B4B8]" />
            <input
              type="text"
              value={quickSearch}
              onChange={e => setQuickSearch(e.target.value)}
              placeholder="Search for a procedure, e.g. knee replacement…"
              className="w-full pl-10 pr-4 py-3 border border-[#A8B4B8] bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A] transition-colors"
              data-testid="quick-search-input"
            />
          </div>
          <button
            type="submit"
            className="bg-[#0D3D4A] text-white px-6 py-3 text-sm font-medium hover:bg-[#0E4557] transition-colors"
            data-testid="quick-search-submit"
          >
            Search
          </button>
        </form>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { label: 'Find Procedures', desc: 'Browse or AI-search', icon: Search, to: '/procedures', color: 'bg-[#0D3D4A]' },
            { label: 'Compare Costs', desc: 'Cash vs insurance', icon: DollarSign, to: '/compare', color: 'bg-[#0D3D4A]' },
            { label: 'Find Providers', desc: 'All 50 states', icon: ArrowRight, to: '/providers', color: 'bg-stone-700' },
            { label: 'AI Assistant', desc: 'Get guidance', icon: BookMarked, to: '/ai-assistant', color: 'bg-stone-600' },
          ].map((action, i) => (
            <button
              key={i}
              onClick={() => navigate(action.to)}
              className={`${action.color} text-white p-6 text-left hover:opacity-90 transition-opacity`}
              data-testid={`quick-action-${i}`}
            >
              <action.icon size={20} className="mb-3 opacity-80" />
              <p className="font-semibold text-sm">{action.label}</p>
              <p className="text-xs opacity-70 mt-0.5">{action.desc}</p>
            </button>
          ))}
        </div>

        {/* Price Alerts Card */}
        <div className="mb-10">
          <h2
            className="text-2xl font-semibold text-stone-900 mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Price Alerts
          </h2>
          <div
            className="bg-white border border-stone-200 p-6 flex items-center justify-between cursor-pointer hover:border-[#0D3D4A] transition-colors"
            onClick={() => navigate('/my-alerts')}
            data-testid="alerts-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#0D3D4A]/10 flex items-center justify-center">
                <Bell size={22} className="text-[#0D3D4A]" />
              </div>
              <div>
                {loading ? (
                  <div className="h-6 w-24 bg-stone-100 animate-pulse rounded" />
                ) : (
                  <p className="text-2xl font-semibold text-stone-900">{alertCount}</p>
                )}
                <p className="text-sm text-stone-500">
                  {alertCount === 1 ? 'active price alert' : 'active price alerts'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 text-sm text-[#0D3D4A] font-medium">
              Manage <ArrowRight size={14} />
            </div>
          </div>
        </div>

        {/* Saved Searches */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2
              className="text-2xl font-semibold text-stone-900"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Saved Searches
            </h2>
            <span className="text-sm text-stone-500">{savedSearches.length} saved</span>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white border border-stone-200 p-6 animate-pulse">
                  <div className="h-4 bg-stone-100 rounded w-3/4 mb-3" />
                  <div className="h-3 bg-stone-100 rounded w-full mb-2" />
                  <div className="h-3 bg-stone-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : savedSearches.length === 0 ? (
            <div className="bg-white border border-stone-200 p-16 text-center">
              <BookMarked size={40} className="text-stone-200 mx-auto mb-4" />
              <p className="text-[#A8B4B8] font-medium mb-2">No saved searches yet</p>
              <p className="text-[#A8B4B8] text-sm mb-6">
                Search for a procedure to get started.
              </p>
              <button
                onClick={() => navigate('/procedures')}
                className="bg-[#0D3D4A] text-white px-6 py-3 text-sm font-medium hover:bg-[#0E4557] transition-colors"
                data-testid="start-searching-btn"
              >
                Find a Procedure
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedSearches.map(search => (
                <div
                  key={search.id}
                  className="bg-white border border-stone-200 p-6 hover:border-stone-300 transition-colors"
                  data-testid={`saved-search-${search.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3
                        className="font-semibold text-stone-900 text-sm mb-1"
                        style={{ fontFamily: 'DM Sans, sans-serif' }}
                      >
                        {search.procedure_name}
                      </h3>
                      {search.procedure_cpt && (
                        <p className="text-xs text-[#A8B4B8] font-mono">CPT {search.procedure_cpt}</p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteSearch(search.id)}
                      className="text-[#A8B4B8] hover:text-red-400 transition-colors ml-2"
                      data-testid={`delete-search-${search.id}`}
                      title="Remove"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  {search.home_state && (
                    <p className="text-xs text-stone-500 mb-3">
                      State: <span className="font-medium text-stone-700">{search.home_state}</span>
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                    <div className="flex items-center gap-1 text-xs text-[#A8B4B8]">
                      <Calendar size={11} />
                      {new Date(search.created_at).toLocaleDateString()}
                    </div>
                    <button
                      onClick={() =>
                        navigate(
                          search.procedure_cpt
                            ? `/search-results?q=${encodeURIComponent(search.procedure_name)}`
                            : `/search-results?q=${encodeURIComponent(search.procedure_name)}`
                        )
                      }
                      className="text-xs text-[#0D3D4A] font-medium flex items-center gap-1 hover:underline"
                    >
                      Search again <ArrowRight size={11} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
